import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, EmptyRow } from "@/components/shared";
import { F, In, Sel, DelBtn, Td, TableCard } from "@/pages/Feed";
import { toast } from "sonner";
import { Plus, ShieldAlert } from "lucide-react";

const APP_LABEL = { suya: "Suya", yeme: "Yeme", enjeksiyon: "Enjeksiyon" };

export default function Medicine() {
  const { coops, myCoops, coopName, selectedCoop, meta } = useApp();
  const activeCoops = myCoops.filter((c) => c.type === "coop");
  const [tab, setTab] = useState("cards");
  const [meds, setMeds] = useState([]);
  const [apps, setApps] = useState([]);
  const [mForm, setMForm] = useState({ name: "", active_ingredient: "", stock_qty: "", unit: "ml", expiry_date: "", application_type: "suya" });
  const [aForm, setAForm] = useState({ coop_id: "", medicine_id: "", diagnosis: "", dosage: "", start_date: new Date().toISOString().slice(0, 10), end_date: "", withdrawal_days: "" });

  const load = useCallback(async () => {
    const q = selectedCoop !== "all" ? `?coop_id=${selectedCoop}` : "";
    const [m, a] = await Promise.all([api.get("/medicines"), api.get(`/medicine-applications${q}`)]);
    setMeds(m.data); setApps(a.data);
  }, [selectedCoop]);
  useEffect(() => { load(); }, [load]);

  const saveMed = async () => {
    if (!mForm.name) return toast.error("İlaç adı zorunlu");
    try {
      await api.post("/medicines", { ...mForm, stock_qty: Number(mForm.stock_qty || 0), expiry_date: mForm.expiry_date || null });
      toast.success("İlaç kartı eklendi");
      setMForm({ name: "", active_ingredient: "", stock_qty: "", unit: "ml", expiry_date: "", application_type: "suya" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveApp = async () => {
    if (!aForm.coop_id || !aForm.medicine_id) return toast.error("Kümes ve ilaç seçin");
    try {
      await api.post("/medicine-applications", { ...aForm, withdrawal_days: Number(aForm.withdrawal_days || 0), end_date: aForm.end_date || null });
      toast.success("Uygulama kaydedildi");
      setAForm({ ...aForm, diagnosis: "", dosage: "", end_date: "", withdrawal_days: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <PageHeader title="İlaç, Aşı & Veterinerlik" subtitle="İlaç kartları, uygulama günlüğü ve arınma süresi takibi" />
      <div className="flex gap-2 mb-4">
        <TabBtn active={tab === "cards"} onClick={() => setTab("cards")} testid="med-tab-cards">İlaç / Aşı Kartları</TabBtn>
        <TabBtn active={tab === "apps"} onClick={() => setTab("apps")} testid="med-tab-apps">Uygulama Günlüğü</TabBtn>
      </div>

      {tab === "cards" ? (
        <>
          <div className="rounded-xl border border-border bg-card p-5 mb-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 items-end" data-testid="med-card-form">
            <F label="İlaç Adı"><In value={mForm.name} onChange={(v) => setMForm({ ...mForm, name: v })} testid="mc-name" /></F>
            <F label="Etken Madde"><In value={mForm.active_ingredient} onChange={(v) => setMForm({ ...mForm, active_ingredient: v })} testid="mc-ing" /></F>
            <F label="Stok"><In type="number" value={mForm.stock_qty} onChange={(v) => setMForm({ ...mForm, stock_qty: v })} testid="mc-stock" /></F>
            <F label="Birim"><In value={mForm.unit} onChange={(v) => setMForm({ ...mForm, unit: v })} testid="mc-unit" /></F>
            <F label="SKT"><In type="date" value={mForm.expiry_date} onChange={(v) => setMForm({ ...mForm, expiry_date: v })} testid="mc-expiry" /></F>
            <F label="Uygulama"><select value={mForm.application_type} onChange={(e) => setMForm({ ...mForm, application_type: e.target.value })} data-testid="mc-apptype" className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none">{meta.application_types.map((t) => <option key={t} value={t}>{APP_LABEL[t]}</option>)}</select></F>
            <button onClick={saveMed} data-testid="mc-save" className="h-9 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={16} /> Ekle</button>
          </div>
          <TableCard headers={["İlaç", "Etken Madde", "Stok", "SKT", "Uygulama", ""]}>
            {meds.length === 0 ? <EmptyRow colSpan={6} /> : meds.map((m) => (
              <tr key={m.id} className="border-t border-border hover:bg-secondary/30" data-testid={`med-row-${m.id}`}>
                <Td className="font-medium">{m.name}</Td><Td>{m.active_ingredient}</Td>
                <Td>{m.stock_qty} {m.unit}</Td>
                <Td className={m.expiry_date && m.expiry_date <= today ? "text-destructive font-medium" : ""}>{m.expiry_date || "-"}</Td>
                <Td>{APP_LABEL[m.application_type]}</Td>
                <Td><DelBtn onClick={async () => { await api.delete(`/medicines/${m.id}`); load(); }} /></Td>
              </tr>
            ))}
          </TableCard>
        </>
      ) : (
        <>
          <div className="rounded-xl border border-border bg-card p-5 mb-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 items-end" data-testid="med-app-form">
            <F label="Kümes"><Sel value={aForm.coop_id} onChange={(v) => setAForm({ ...aForm, coop_id: v })} opts={activeCoops} testid="ma-coop" /></F>
            <F label="İlaç"><select value={aForm.medicine_id} onChange={(e) => setAForm({ ...aForm, medicine_id: e.target.value })} data-testid="ma-med" className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none"><option value="">Seçin</option>{meds.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></F>
            <F label="Teşhis/Amaç"><In value={aForm.diagnosis} onChange={(v) => setAForm({ ...aForm, diagnosis: v })} testid="ma-diag" /></F>
            <F label="Dozaj"><In value={aForm.dosage} onChange={(v) => setAForm({ ...aForm, dosage: v })} testid="ma-dose" /></F>
            <F label="Başlangıç"><In type="date" value={aForm.start_date} onChange={(v) => setAForm({ ...aForm, start_date: v })} testid="ma-start" /></F>
            <F label="Bitiş"><In type="date" value={aForm.end_date} onChange={(v) => setAForm({ ...aForm, end_date: v })} testid="ma-end" /></F>
            <F label="Arınma (gün)"><In type="number" value={aForm.withdrawal_days} onChange={(v) => setAForm({ ...aForm, withdrawal_days: v })} testid="ma-wd" /></F>
            <button onClick={saveApp} data-testid="ma-save" className="h-9 col-span-2 md:col-span-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={16} /> Kaydet</button>
          </div>
          <TableCard headers={["Kümes", "İlaç", "Teşhis", "Dozaj", "Başlangıç", "Bitiş", "Arınma Bitişi", ""]}>
            {apps.length === 0 ? <EmptyRow colSpan={8} /> : apps.map((a) => {
              const active = a.withdrawal_end_date && a.withdrawal_end_date >= today;
              return (
                <tr key={a.id} className="border-t border-border hover:bg-secondary/30" data-testid={`ma-row-${a.id}`}>
                  <Td>{coopName(a.coop_id)}</Td><Td className="font-medium">{a.medicine_name}</Td><Td>{a.diagnosis}</Td><Td>{a.dosage}</Td>
                  <Td>{a.start_date}</Td><Td>{a.end_date || "-"}</Td>
                  <Td>{a.withdrawal_end_date ? (active ? <span className="inline-flex items-center gap-1 text-destructive font-medium"><ShieldAlert size={14} />{a.withdrawal_end_date}</span> : a.withdrawal_end_date) : "-"}</Td>
                  <Td><DelBtn onClick={async () => { await api.delete(`/medicine-applications/${a.id}`); load(); }} /></Td>
                </tr>
              );
            })}
          </TableCard>
        </>
      )}
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{children}</button>
);
